import { useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { CheckCircle, AlertCircle, Loader2, RefreshCw, Upload } from 'lucide-react'
import type { UploadState } from '@/hooks/useOptimisticUpload'
import type { UploadTrack } from '@/hooks/useMultiUpload'

interface UploadStatusIndicatorProps {
  state: UploadState
  onRetry?: () => void
  onDismiss?: () => void
  autoDismissDelay?: number // Auto-dismiss success after this many ms (default: 3000)
  label?: string // Custom label (e.g., "Photo" or "Permission Form 1")
}

export function UploadStatusIndicator({
  state,
  onRetry,
  onDismiss,
  autoDismissDelay = 3000,
  label = 'photo',
}: UploadStatusIndicatorProps) {
  // Auto-dismiss on success
  useEffect(() => {
    if (state.status === 'success' && autoDismissDelay > 0 && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss()
      }, autoDismissDelay)
      return () => clearTimeout(timer)
    }
  }, [state.status, autoDismissDelay, onDismiss])

  // Don't show anything if idle
  if (state.status === 'idle') {
    return null
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed right-4 bottom-4 z-50"
      >
        <div className="bg-surface border-border min-w-[200px] rounded-xl border p-3 shadow-lg">
          {state.status === 'uploading' && (
            <div className="flex items-center gap-3">
              <div className="relative">
                <Loader2 className="text-accent h-5 w-5 animate-spin" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-foreground text-sm font-medium">Uploading {label}...</p>
                <div className="bg-surface-hover mt-1.5 h-1.5 overflow-hidden rounded-full">
                  <motion.div
                    className="bg-accent h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${state.progress}%` }}
                    transition={{ duration: 0.2 }}
                  />
                </div>
                <p className="text-foreground-muted mt-1 text-xs">{state.progress}%</p>
              </div>
            </div>
          )}

          {state.status === 'success' && (
            <div className="flex items-center gap-3">
              <CheckCircle className="text-success h-5 w-5" />
              <div className="flex-1">
                <p className="text-foreground text-sm font-medium">
                  {label.charAt(0).toUpperCase() + label.slice(1)} uploaded
                </p>
                <p className="text-foreground-muted text-xs">Ready for submission</p>
              </div>
            </div>
          )}

          {state.status === 'error' && (
            <div className="flex items-center gap-3">
              <AlertCircle className="text-error h-5 w-5" />
              <div className="flex-1">
                <p className="text-foreground text-sm font-medium">Upload failed</p>
                <p className="text-error text-xs">{state.error || 'Unknown error'}</p>
              </div>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="bg-accent/10 text-accent hover:bg-accent/20 rounded-lg p-1.5 transition-colors"
                  title="Retry upload"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

// Inline version for use within steps (like ReviewStep)
export function UploadStatusInline({ state, onRetry, label = 'photo' }: UploadStatusIndicatorProps) {
  if (state.status === 'idle') {
    return null
  }

  return (
    <div className="border-border bg-surface rounded-xl border p-3">
      {state.status === 'uploading' && (
        <div className="flex items-center gap-3">
          <Upload className="text-accent h-5 w-5 animate-pulse" />
          <div className="min-w-0 flex-1">
            <p className="text-foreground text-sm font-medium">Uploading {label}...</p>
            <div className="bg-surface-hover mt-1.5 h-1.5 overflow-hidden rounded-full">
              <motion.div
                className="bg-accent h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${state.progress}%` }}
                transition={{ duration: 0.2 }}
              />
            </div>
          </div>
          <span className="text-foreground-muted text-sm font-medium">{state.progress}%</span>
        </div>
      )}

      {state.status === 'success' && (
        <div className="flex items-center gap-3">
          <CheckCircle className="text-success h-5 w-5" />
          <p className="text-success text-sm font-medium">
            {label.charAt(0).toUpperCase() + label.slice(1)} uploaded successfully
          </p>
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex items-center gap-3">
          <AlertCircle className="text-error h-5 w-5" />
          <div className="flex-1">
            <p className="text-error text-sm font-medium">Upload failed</p>
            <p className="text-foreground-muted text-xs">{state.error}</p>
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="bg-accent text-accent-foreground hover:bg-accent/90 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Multi-upload status indicator - stacked toasts for multiple uploads
interface MultiUploadStatusIndicatorProps {
  uploads: UploadTrack[]
  onRetry?: (id: string) => void
  onDismiss?: (id: string) => void
  autoDismissDelay?: number
}

export function MultiUploadStatusIndicator({
  uploads,
  onRetry,
  onDismiss,
  autoDismissDelay = 3000,
}: MultiUploadStatusIndicatorProps) {
  // Filter out idle uploads
  const activeUploads = uploads.filter((u) => u.status !== 'idle')

  if (activeUploads.length === 0) {
    return null
  }

  return (
    <div className="fixed right-4 bottom-4 z-50 flex flex-col gap-2">
      <AnimatePresence>
        {activeUploads.map((upload) => (
          <SingleUploadToast
            key={upload.id}
            upload={upload}
            onRetry={onRetry ? () => onRetry(upload.id) : undefined}
            onDismiss={onDismiss ? () => onDismiss(upload.id) : undefined}
            autoDismissDelay={autoDismissDelay}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}

// Single upload toast for use in the multi-upload indicator
function SingleUploadToast({
  upload,
  onRetry,
  onDismiss,
  autoDismissDelay,
}: {
  upload: UploadTrack
  onRetry?: () => void
  onDismiss?: () => void
  autoDismissDelay: number
}) {
  const label = 'Photo'

  // Auto-dismiss on success
  useEffect(() => {
    if (upload.status === 'success' && autoDismissDelay > 0 && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss()
      }, autoDismissDelay)
      return () => clearTimeout(timer)
    }
  }, [upload.status, autoDismissDelay, onDismiss])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className="bg-surface border-border min-w-[220px] rounded-xl border p-3 shadow-lg"
    >
      {upload.status === 'uploading' && (
        <div className="flex items-center gap-3">
          <Loader2 className="text-accent h-5 w-5 animate-spin" />
          <div className="min-w-0 flex-1">
            <p className="text-foreground text-sm font-medium">Uploading {label.toLowerCase()}...</p>
            <div className="bg-surface-hover mt-1.5 h-1.5 overflow-hidden rounded-full">
              <motion.div
                className="bg-accent h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${upload.progress}%` }}
                transition={{ duration: 0.2 }}
              />
            </div>
            <p className="text-foreground-muted mt-1 text-xs">{upload.progress}%</p>
          </div>
        </div>
      )}

      {upload.status === 'success' && (
        <div className="flex items-center gap-3">
          <CheckCircle className="text-success h-5 w-5" />
          <div className="flex-1">
            <p className="text-foreground text-sm font-medium">{label} uploaded</p>
            <p className="text-foreground-muted text-xs">Ready for submission</p>
          </div>
        </div>
      )}

      {upload.status === 'error' && (
        <div className="flex items-center gap-3">
          <AlertCircle className="text-error h-5 w-5" />
          <div className="flex-1">
            <p className="text-foreground text-sm font-medium">{label} upload failed</p>
            <p className="text-error text-xs">{upload.error || 'Unknown error'}</p>
          </div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="bg-accent/10 text-accent hover:bg-accent/20 rounded-lg p-1.5 transition-colors"
              title="Retry upload"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </motion.div>
  )
}
